import { useSyncExternalStore } from 'react'
import type { OrbState, VoiceState } from './types'

const initial: VoiceState = {
	phase: 'entrance',
	entranceLeaving: false,
	orb: 'idle',
	assistantLine: '',
	lineId: 0,
	userEcho: '',
	echoId: 0,
	statusHint: 'none',
	card: null,
	options: null,
	chips: [],
	price: null,
	draft: null,
	success: null,
	sum: {},
	total: '—',
	expanded: false,
	holdOn: false,
	holdLeft: 600,
	listening: false,
	done: false,
	phoneShown: false,
}

type Listener = () => void

class VoiceStore {
	private state: VoiceState = { ...initial }
	private listeners = new Set<Listener>()
	private holdTimer: number | null = null

	get = (): VoiceState => this.state

	set = (patch: Partial<VoiceState>): void => {
		this.state = { ...this.state, ...patch }
		this.listeners.forEach((l) => l())
	}

	subscribe = (l: Listener): (() => void) => {
		this.listeners.add(l)
		return () => this.listeners.delete(l)
	}

	setOrb = (orb: OrbState): void => {
		if (this.state.orb !== orb) this.set({ orb })
	}

	/** New assistant utterance — bumps lineId so the fade-in replays. */
	setLine = (text: string): void => {
		this.set({ assistantLine: text, lineId: this.state.lineId + 1 })
	}

	appendLine = (delta: string): void => {
		this.set({ assistantLine: this.state.assistantLine + delta })
	}

	setEcho = (text: string): void => {
		this.set({ userEcho: text, echoId: this.state.echoId + 1 })
	}

	/** Any transient content on stage → orb shrinks 240 → 110px. */
	busy = (): boolean => {
		const s = this.state
		return !!(s.card || s.options || s.price || s.success || s.draft)
	}

	startHold = (seconds = 600): void => {
		this.stopHold()
		this.set({ holdOn: true, holdLeft: seconds })
		this.holdTimer = window.setInterval(() => {
			const s = this.state
			if (s.done || s.holdLeft <= 0) {
				this.stopHold()
				return
			}
			this.set({ holdLeft: s.holdLeft - 1 })
		}, 1000)
	}

	stopHold = (): void => {
		if (this.holdTimer !== null) {
			window.clearInterval(this.holdTimer)
			this.holdTimer = null
		}
	}

	reset = (): void => {
		this.stopHold()
		this.state = { ...initial }
		this.listeners.forEach((l) => l())
	}
}

export const store = new VoiceStore()

export function useVoiceState(): VoiceState {
	return useSyncExternalStore(store.subscribe, store.get)
}

export function formatHold(seconds: number): string {
	const s = Math.max(0, seconds)
	return `Az időpontot még ${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')} percig tartjuk Önnek`
}
